import { createSlice, configureStore, combineReducers } from '@reduxjs/toolkit';
import { 
  persistStore, 
  persistReducer, 
  FLUSH, 
  REHYDRATE, 
  PAUSE, 
  PERSIST, 
  PURGE, 
  REGISTER,
  createMigrate
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import connectionReducer from './connectionSlice';
import calendarReducer from '../pages/calendar/state';
import notificationReducer from './notificationSlice';
import mealPlannerReducer from './mealPlannerSlice';

// Define initial state for auth
const initialAuthState = {
  user: null,
  token: null,
  profileImage: null,
};

// Create auth slice
export const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    setLogin: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
    setLogout: (state) => {
      state.user = null;
      state.token = null;
      state.profileImage = null;
    },
    updateUser: (state, action) => {
      state.user = action.payload.user;
    },
    setUpdateUser: (state, action) => {
      state.user = action.payload;
    },
    setProfileImage: (state, action) => {
      state.profileImage = action.payload.profileImage;
    },
    // Add a setState action for recovery purposes
    setState: (state, action) => {
      return { ...state, ...action.payload };
    }
  },
});

// Create persistence status slice
export const persistenceSlice = createSlice({
  name: 'persistence',
  initialState: {
    status: 'idle', // 'idle', 'pending', 'success', 'error'
    error: null,
    lastSuccessTime: null
  },
  reducers: {
    persistencePending: (state) => {
      state.status = 'pending';
    },
    persistenceSuccess: (state) => {
      state.status = 'success';
      state.error = null;
      state.lastSuccessTime = Date.now();
    },
    persistenceError: (state, action) => {
      state.status = 'error';
      state.error = action.payload;
    },
    persistenceReset: (state) => {
      state.status = 'idle';
      state.error = null;
    }
  }
});

// Migrations for version updates
const migrations = {
  1: (state) => {
    // Initial migration - just return the state as is
    return state;
  }
};

// Auth persist config
const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['user', 'token', 'profileImage'],
  stateReconciler: autoMergeLevel2,
  version: 1,
  migrate: createMigrate(migrations, { debug: false }),
  writeFailHandler: (err) => {
    console.error('Auth persistence write failure:', err);
    // Dispatch error action if store is available
    if (store) {
      store.dispatch(persistenceSlice.actions.persistenceError(err.message || 'Storage write failed'));
    }
  },
};

// Notification persist config
const notificationPersistConfig = {
  key: 'notifications',
  storage,
  whitelist: ['notifications', 'unreadCount'],
  stateReconciler: autoMergeLevel2,
  version: 1
};

// Create root reducer with persistence
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authSlice.reducer),
  persistence: persistenceSlice.reducer,
  calendar: calendarReducer,
  connections: connectionReducer,
  notifications: persistReducer(notificationPersistConfig, notificationReducer),
  mealPlanner: mealPlannerReducer
});

// Create the store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Create the persistor
export const persistor = persistStore(store);

// Make store available in global window object for external components like SocketManager
if (typeof window !== 'undefined') {
  window.reduxStore = store;
}

// Function to attempt recovery of persistence issues
export const attemptPersistenceRecovery = () => {
  console.log('Attempting persistence recovery');
  
  try {
    // Get current auth state
    const authState = store.getState().auth;
    
    // Create a clean state with only essential data
    const recoveredState = {
      ...initialAuthState,
      // Keep user ID if available, but clean other data
      user: authState?.user?.id ? { id: authState.user.id, name: authState.user.name } : null,
      token: authState?.token // Keep token if available
    };
    
    // Update store with recovered state
    store.dispatch(authSlice.actions.setState(recoveredState));
    
    // Try persistence again
    persistor.persist();
    
    return true;
  } catch (error) {
    console.error('Recovery attempt failed:', error);
    return false;
  }
};

// Function to clear persisted state - enhanced for cross-browser compatibility
export const clearPersistedState = () => {
  console.log('Clearing persisted state');
  
  // First dispatch logout action
  store.dispatch(authSlice.actions.setLogout());
  
  // Reset persistence status
  store.dispatch(persistenceSlice.actions.persistenceReset());
  
  // Clear all auth-related localStorage items with try/catch for each operation
  const itemsToClear = [
    'persist:auth',
    'rememberedEmail',
    'hasRememberedCredentials',
    'persist:root' // Clear the root persist if it exists
  ];
  
  itemsToClear.forEach(item => {
    try {
      localStorage.removeItem(item);
      console.log(`Successfully cleared ${item} from localStorage`);
    } catch (e) {
      console.error(`Error clearing ${item} from localStorage:`, e);
    }
  });
  
  // Try to clear all session storage as well
  try {
    sessionStorage.clear();
    console.log('Successfully cleared sessionStorage');
  } catch (e) {
    console.error('Error clearing sessionStorage:', e);
  }
  
  // Force persistor to purge
  try {
    persistor.purge();
    console.log('Successfully purged persistor');
  } catch (e) {
    console.error('Error purging persistor:', e);
  }
};

export const { setLogin, setLogout, updateUser, setUpdateUser, setProfileImage, setState } = authSlice.actions;
export const { 
  persistencePending, 
  persistenceSuccess, 
  persistenceError, 
  persistenceReset 
} = persistenceSlice.actions;

export default store;

